import { useQuery } from '@tanstack/react-query'
import type { UseQueryOptions } from '@tanstack/react-query'

import type {
  ApiError,
  ExtensionInstance,
  PaginatedApiResult,
} from '@mangovoice/types'

import { useExtensionsApi } from '../api/context'
import { EXTENSION_QUERY_KEYS } from '../api/endpoints'
import type { ExtensionsListParams, LineFilterOption } from '../types/api.types'

interface UseListExtensionsParams {
  pbxId: number | undefined | null
  listParams?: ExtensionsListParams
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<ExtensionInstance>, ApiError>,
    'queryKey'
  >
}

/**
 * Hook to list extensions for a PBX (for Extensions list page).
 * Uses GET /app/v1/extensions/ with pbx_id, search, ordering, limit, offset (server-side).
 */
export const useListExtensions = ({
  pbxId,
  listParams = {},
  options,
}: UseListExtensionsParams) => {
  const api = useExtensionsApi()
  const {
    search,
    ordering = '-updated_at',
    limit = 25,
    offset = 0,
    fetchAll = false,
    type,
    statusFilter,
    assignedUserFilter,
    locationFilter,
  } = listParams

  const query = useQuery<PaginatedApiResult<ExtensionInstance>, ApiError>({
    queryKey: EXTENSION_QUERY_KEYS.list(
      pbxId,
      listParams as Record<string, unknown>,
    ),
    queryFn: async () => {
      const baseParams = {
        pbx_id: pbxId ?? undefined,
        search: search?.trim() || undefined,
        ordering,
        type: type ?? undefined,
        status_filter: statusFilter,
        assigned_user_id: assignedUserFilter?.length
          ? assignedUserFilter
          : undefined,
        location_id: locationFilter?.length ? locationFilter : undefined,
      }

      if (!fetchAll) {
        const { ok, data } = await api.getExtensions({
          ...baseParams,
          limit,
          offset,
        })

        if (!ok || !data) {
          return Promise.reject(
            (data as ApiError) ?? {
              code: 'fetch_error',
              message: 'Failed to fetch extensions',
            },
          )
        }

        return data as PaginatedApiResult<ExtensionInstance>
      }

      const pageLimit = Math.max(1, limit || 250)
      let nextOffset = Math.max(0, offset || 0)
      let count = 0
      const allResults: ExtensionInstance[] = []

      for (let page = 0; page < 200; page += 1) {
        const { ok, data } = await api.getExtensions({
          ...baseParams,
          limit: pageLimit,
          offset: nextOffset,
        })

        if (!ok || !data) {
          return Promise.reject(
            (data as ApiError) ?? {
              code: 'fetch_error',
              message: 'Failed to fetch extensions',
            },
          )
        }

        const pageData = data as PaginatedApiResult<ExtensionInstance>
        count = pageData.count
        allResults.push(...(pageData.results ?? []))

        if (!pageData.next || allResults.length >= count) {
          break
        }

        const nextUrl = new URL(pageData.next, window.location.origin)
        const nextOffsetParam = nextUrl.searchParams.get('offset')
        const parsedNextOffset = nextOffsetParam
          ? parseInt(nextOffsetParam, 10)
          : Number.NaN
        nextOffset = Number.isNaN(parsedNextOffset)
          ? nextOffset + pageLimit
          : parsedNextOffset
      }

      return {
        count,
        next: null,
        previous: null,
        results: allResults,
      } satisfies PaginatedApiResult<ExtensionInstance>
    },
    enabled: pbxId != null && !Number.isNaN(pbxId),
    ...options,
    retry: options?.retry ?? false,
  })

  return {
    ...query,
    extensions: query.data?.results ?? [],
    count: query.data?.count ?? 0,
    isLoading: query.isLoading,
    error: query.error as ApiError | null,
  }
}

interface UseGetExtensionInfoParams {
  extensionId: number | string
  options?: Omit<UseQueryOptions<ExtensionInstance, ApiError>, 'queryKey'>
}

/**
 * Hook to get extension information by ID
 */
export const useGetExtensionInfo = ({
  extensionId,
  options,
}: UseGetExtensionInfoParams) => {
  const api = useExtensionsApi()

  return useQuery<ExtensionInstance, ApiError>({
    queryKey: EXTENSION_QUERY_KEYS.detail(extensionId),
    queryFn: async () => {
      const { ok, data } = await api.getExtensionInfo(extensionId)

      if (!ok) {
        return Promise.reject(data as ApiError)
      }

      return data as ExtensionInstance
    },
    enabled: !!extensionId,
    ...options,
    retry: options?.retry ?? false,
  })
}

/**
 * Loads line-derived filter options from API data.
 * Uses server-side pagination to fetch all line rows, then deduplicates users and locations.
 */
export const useLineFilterOptions = (pbxId: number | undefined | null) => {
  const { extensions, isLoading } = useListExtensions({
    pbxId,
    listParams: {
      type: 'ExtensionLine' as unknown as string,
      fetchAll: true,
      limit: 250,
      ordering: 'name',
    },
    options: { enabled: pbxId != null && !Number.isNaN(pbxId) },
  })

  const usersMap = new Map<number, string>()
  const locationsMap = new Map<number, string>()

  extensions.forEach((line) => {
    const lineWithOptionals = line as ExtensionInstance & {
      assigned_user?: { id: number; name: string }
      location?: { id: number; name: string }
    }

    if (lineWithOptionals.assigned_user?.id != null) {
      usersMap.set(
        lineWithOptionals.assigned_user.id,
        lineWithOptionals.assigned_user.name ?? `User ${line.id}`,
      )
    }

    if (lineWithOptionals.location?.id != null) {
      locationsMap.set(
        lineWithOptionals.location.id,
        lineWithOptionals.location.name ?? `Location ${line.id}`,
      )
    }
  })

  const users: LineFilterOption[] = Array.from(usersMap.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))

  const locations: LineFilterOption[] = Array.from(locationsMap.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))

  return {
    extensions,
    users,
    locations,
    isLoading,
  }
}
